// Consent-gated loader for Google Analytics (GA4/gtag) — SEC-6.
//
// gtag is loaded imperatively (rather than via a hard-coded <script> tag) so it
// only ever reaches Google once analytics consent is granted, and so the gtag
// stub + `config` run in a deterministic order before any event is fired.
//
// Importing this module self-registers the loader against the consent store; it
// is a no-op until consent is granted, and safe to import in SSR/test contexts.
import { whenAnalyticsConsentGranted } from './analyticsConsent';

const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

let gtagLoaded = false;

/**
 * Ensure the gtag stub exists and queue the standard bootstrap calls. Idempotent
 * and safe to call before the async gtag.js library has finished loading — events
 * pushed to `dataLayer` are replayed once the library and `config` are processed.
 */
export function loadGtag(): void {
  if (typeof window === 'undefined' || !GA4_MEASUREMENT_ID || gtagLoaded) return;
  gtagLoaded = true;

  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  // Do not clobber an existing stub (e.g. one created by a queued event).
  w.gtag = w.gtag || function gtag() {
    w.dataLayer.push(arguments);
  };
  w.gtag('js', new Date());
  w.gtag('config', GA4_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

// Register early (at import time) so `config` runs before any consent-gated
// event (e.g. the sign_up conversion) is flushed.
whenAnalyticsConsentGranted(loadGtag);
